import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import patchUpdateCharacter from '@cartwhl/common/api/patchUpdateCharacter';
import type { MotionConfigSource } from '@cartwhl/common/api/getMotion';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { pushCartographerDebugEvent } from '../../lib/cartographerDebug';
import type { ViewerToolDockMotionSectionProps } from '../Viewer';
import {
  AssetLibraryModal,
  createMogenMotionPickerEmbedConfig,
} from '../../../AssetLibrary';
import CartographerMotionActorPickerModal from './components/CartographerMotionActorPickerModal';
import CartographerMotionMini from './components/CartographerMotionMini';
import CartographerMotionPreviewModal from './components/CartographerMotionPreviewModal';
import CartographerSceneMotionPreview from './components/CartographerSceneMotionPreview';
import {
  ENABLE_DOCKED_MINI_MOTION_PREVIEW,
  ENABLE_STAGE_MOTION_PREVIEW,
} from './constants';
import {
  buildCharacterMotionExportFilename,
  DEFAULT_MOTION_EXPORT_SETTINGS,
  downloadCharacterMotionExport,
  getCharacterMeshDownloadProfile,
  type MotionExportSettings,
} from './motionExport';
import { resolveMotionPreviewAssetKind } from './motionPreviewAsset';
import {
  getMotionExportSettingsKey,
  getMotionRetargetStatusKey,
  type CartographerMotionRetargetStatus,
} from './motionRetargetStatus';
import useCartographerMotionWorkflow from './useCartographerMotionWorkflow';

interface UseCartographerMotionSectionOptions {
  apiKey: string | null;
  organizationServiceId: string | null;
  loadedCharacterID: string | null;
  activeCharacterFile: File | null;
  hasGltfScene: boolean;
  isViewerToolDockMounted: boolean;
  isCharacterDirty: boolean;
  frontDoorRef: RefObject<HTMLDivElement | null>;
  frontDoorHeight: number;
  frontDoorWidth: number;
  fileVersion: number;
  mappingVersion: number;
  bonePropertiesVersion: number;
  fileName: string | null;
  loading: boolean;
  saveInProgress: boolean;
  isCharacterSwitchActive: boolean;
  handleSaveRef: MutableRefObject<() => Promise<boolean>>;
  onClearError: () => void;
  onSetError: (message: string) => void;
  exportCurrentMappingConfig: () => string | null;
  buildPresignedPutOptions: (
    body: Blob | File,
    contentType: string,
  ) => {
    method: 'PUT';
    body: Blob | File;
    headers: { 'Content-Type': string };
  };
}

export default function useCartographerMotionSection({
  apiKey,
  organizationServiceId,
  loadedCharacterID,
  activeCharacterFile,
  hasGltfScene,
  isViewerToolDockMounted,
  isCharacterDirty,
  frontDoorRef,
  frontDoorHeight,
  frontDoorWidth,
  fileVersion,
  mappingVersion,
  bonePropertiesVersion,
  fileName,
  loading,
  saveInProgress,
  isCharacterSwitchActive,
  handleSaveRef,
  onClearError,
  onSetError,
  exportCurrentMappingConfig,
  buildPresignedPutOptions,
}: UseCartographerMotionSectionOptions) {
  const [viewMotionSaveInProgress, setViewMotionSaveInProgress] =
    useState(false);
  const [motionExportSettings, setMotionExportSettings] =
    useState<MotionExportSettings>(DEFAULT_MOTION_EXPORT_SETTINGS);
  const [motionExportBusy, setMotionExportBusy] = useState(false);
  const [motionStagePlaybackActive, setMotionStagePlaybackActive] =
    useState(false);
  const [latestRetargetStatus, setLatestRetargetStatus] =
    useState<CartographerMotionRetargetStatus | null>(null);
  const [lastReadyRetargetStatus, setLastReadyRetargetStatus] =
    useState<CartographerMotionRetargetStatus | null>(null);
  const {
    activateUnsavedMotionPreview,
    actorPickerSelection,
    backFromMotionPreviewToAssetLibrary,
    cartographerAssetLibraryMode,
    closeAssetLibrary,
    closeMotionPreview,
    handleActorPickerOpenChange,
    handleActorPickerSelect,
    handleAssetLibraryPick,
    handleOpenFullMotionPreviewFromMini,
    motionMiniToggleDisabled,
    motionMiniToggleLabel,
    motionPreviewCharacterMappingState,
    motionPreviewExternalJobId,
    motionPreviewExternalLabel,
    motionPreviewMeshFile,
    motionPreviewMeshFormat,
    motionPreviewMeshUrl,
    motionPreviewOpen,
    motionPreviewSession,
    openAssetLibrary,
    requestOpenAssetLibrary,
    previewOptionsOpen,
    previewSurface,
    rememberAndClearPreviewSurface,
    resetMotionMiniState,
    selectedBodyIndex,
    selectedMotionId,
    selectedMotionLabel,
    setPreviewSurface,
    setViewMotionUnsavedModalOpen,
    toggleMotionMini,
    toggleToolDockMotionPreview,
    viewMotionUnsavedModalOpen,
  } = useCartographerMotionWorkflow({
    apiKey,
    organizationServiceId,
    loadedCharacterID,
    activeCharacterFile,
    hasGltfScene,
    isCharacterDirty,
    frontDoorRef,
    onClearError,
  });
  const motionPreviewMappingRevisionKey = `${mappingVersion}|${bonePropertiesVersion}`;
  const motionPreviewConfigSource: MotionConfigSource =
    motionPreviewCharacterMappingState === 'unsaved' ? 'temp' : 'saved';
  const currentRetargetStatusKey = getMotionRetargetStatusKey({
    motionID: selectedMotionId,
    bodyIndex: selectedBodyIndex,
    configSource: motionPreviewConfigSource,
    mappingRevisionKey: motionPreviewMappingRevisionKey,
    exportSettingsKey: getMotionExportSettingsKey(motionExportSettings),
  });
  const isLatestRetargetStatusCurrent =
    latestRetargetStatus != null &&
    getMotionRetargetStatusKey(latestRetargetStatus) ===
      currentRetargetStatusKey;
  const currentRetargetStatus = isLatestRetargetStatusCurrent
    ? latestRetargetStatus
    : null;
  const isLastReadyRetargetStatusCurrent =
    lastReadyRetargetStatus != null &&
    getMotionRetargetStatusKey(lastReadyRetargetStatus) ===
      currentRetargetStatusKey;
  const currentReadyRetargetStatus = isLastReadyRetargetStatusCurrent
    ? lastReadyRetargetStatus
    : null;
  const activeMeshDownloadProfile = useMemo(
    () =>
      activeCharacterFile
        ? getCharacterMeshDownloadProfile(undefined, activeCharacterFile.name)
        : null,
    [activeCharacterFile],
  );
  const motionPreviewAssetKind = useMemo(
    () =>
      resolveMotionPreviewAssetKind({
        fileName: activeCharacterFile?.name ?? null,
      }),
    [activeCharacterFile],
  );
  const userCharacterDisplayName = fileName
    ? fileName.replace(/\.[^/.]+$/i, '')
    : null;
  const motionExportFilename =
    activeMeshDownloadProfile != null
      ? buildCharacterMotionExportFilename({
          characterID: loadedCharacterID,
          userCharacterDisplayName,
          motionID: selectedMotionId,
          motionLabel: selectedMotionLabel,
          fileExtension: activeMeshDownloadProfile.fileExtension,
        })
      : '';
  const canShowMotionMini = Boolean(
    ENABLE_DOCKED_MINI_MOTION_PREVIEW && hasGltfScene && activeCharacterFile,
  );
  const canRenderMotionMini =
    canShowMotionMini && !loading && !isCharacterSwitchActive;
  const canRenderMotionStage =
    ENABLE_STAGE_MOTION_PREVIEW &&
    hasGltfScene &&
    !loading &&
    !isCharacterSwitchActive;
  const shouldMountDockedMotionMini =
    canRenderMotionMini && Boolean(selectedMotionId);
  const showDockedMotionMini =
    shouldMountDockedMotionMini &&
    previewSurface === 'mini' &&
    !motionPreviewOpen &&
    cartographerAssetLibraryMode === '';
  // Wait until the dock content remounts after the picker fades out, otherwise
  // the stage footer is briefly laid out against the picker's tall container.
  const showStageMotionPreview =
    canRenderMotionStage &&
    isViewerToolDockMounted &&
    previewSurface === 'stage' &&
    Boolean(selectedMotionId) &&
    !motionPreviewOpen &&
    cartographerAssetLibraryMode === '';

  useEffect(() => {
    pushCartographerDebugEvent('motionStage.visibility', {
      loadedCharacterID,
      selectedMotionId,
      previewSurface,
      canRenderMotionStage,
      isViewerToolDockMounted,
      motionPreviewOpen,
      cartographerAssetLibraryMode,
      showStageMotionPreview,
    });
  }, [
    canRenderMotionStage,
    cartographerAssetLibraryMode,
    isViewerToolDockMounted,
    loadedCharacterID,
    motionPreviewOpen,
    previewSurface,
    selectedMotionId,
    showStageMotionPreview,
  ]);

  const uploadTempConfigForRetarget = useCallback(async () => {
    if (!apiKey || !loadedCharacterID) {
      onSetError('Save this character before previewing unsaved retargeting.');
      return false;
    }

    const configJson = exportCurrentMappingConfig();
    if (!configJson) {
      onSetError('No character mapping is available for temp retarget preview.');
      return false;
    }

    onClearError();

    const { data, error } = await patchUpdateCharacter(apiKey, loadedCharacterID, {
      requestTempConfigUploadURL: true,
    });
    if (error || !data?.tempConfigUploadURL) {
      onSetError(
        error?.message ?? 'Failed to prepare temporary retarget config upload.',
      );
      return false;
    }

    const configBlob = new Blob([configJson], {
      type: 'application/json',
    });
    const putResConfig = await fetch(
      data.tempConfigUploadURL,
      buildPresignedPutOptions(configBlob, 'application/json'),
    );
    if (!putResConfig.ok) {
      onSetError(
        `Failed to upload temp config: ${putResConfig.status} ${putResConfig.statusText}`,
      );
      return false;
    }

    return true;
  }, [
    apiKey,
    buildPresignedPutOptions,
    exportCurrentMappingConfig,
    loadedCharacterID,
    onClearError,
    onSetError,
  ]);

  const prepareForMotionRetargetRequest = useCallback(
    async ({
      configSource,
    }: {
      configSource: MotionConfigSource;
      mode: 'auto' | 'manual';
    }) => {
      if (configSource !== 'temp') {
        return true;
      }
      return await uploadTempConfigForRetarget();
    },
    [uploadTempConfigForRetarget],
  );

  const handleSwitchToUnsavedRetarget = useCallback(async () => {
    if (!isCharacterDirty) {
      return true;
    }
    return activateUnsavedMotionPreview();
  }, [activateUnsavedMotionPreview, isCharacterDirty]);

  useEffect(() => {
    setMotionExportSettings(DEFAULT_MOTION_EXPORT_SETTINGS);
    setMotionExportBusy(false);
    setLatestRetargetStatus(null);
    setLastReadyRetargetStatus(null);
  }, [
    activeCharacterFile,
    loadedCharacterID,
    motionPreviewConfigSource,
    motionPreviewMappingRevisionKey,
    selectedBodyIndex,
    selectedMotionId,
  ]);

  const handleMotionRetargetStatusChange = useCallback(
    (status: CartographerMotionRetargetStatus) => {
      setLatestRetargetStatus(status);
      setLastReadyRetargetStatus((previousStatus) => {
        const statusKey = getMotionRetargetStatusKey(status);
        const isReady = Boolean(
          status.motionID &&
            status.userMotionUrl &&
            !status.mainError &&
            !status.previewOutOfDate,
        );

        if (isReady) return status;
        if (
          previousStatus &&
          getMotionRetargetStatusKey(previousStatus) === statusKey &&
          (status.mainError || status.previewOutOfDate)
        ) {
          return null;
        }
        return previousStatus;
      });
    },
    [],
  );

  const handleDockMotionExport = useCallback(async () => {
    setMotionExportBusy(true);
    onClearError();
    try {
      const result = await downloadCharacterMotionExport({
        apiKey,
        characterID: loadedCharacterID,
        motionID: selectedMotionId,
        bodyIndex: selectedBodyIndex,
        configSource: motionPreviewConfigSource,
        meshDownloadProfile: activeMeshDownloadProfile,
        settings: motionExportSettings,
        userMotionUrl: currentReadyRetargetStatus?.userMotionUrl ?? null,
        filename: motionExportFilename,
        prepareForUserMotionRequest: prepareForMotionRetargetRequest,
      });
      if (!result.ok && result.error) {
        onSetError(result.error);
      }
    } finally {
      setMotionExportBusy(false);
    }
  }, [
    activeMeshDownloadProfile,
    apiKey,
    currentReadyRetargetStatus,
    loadedCharacterID,
    motionExportFilename,
    motionExportSettings,
    motionPreviewConfigSource,
    onClearError,
    onSetError,
    prepareForMotionRetargetRequest,
    selectedBodyIndex,
    selectedMotionId,
  ]);

  const handleMotionMiniRuntimeError = useCallback(
    (error: Error, info?: { componentStack?: string | null }) => {
      console.error('[Cartographer] Motion mini preview runtime error', error);
      pushCartographerDebugEvent('motionMini.runtime.error', {
        error: `${error.name}: ${error.message}`,
        errorStack: error.stack ?? null,
        componentStack: info?.componentStack ?? null,
        loadedCharacterID,
        selectedMotionId,
      });
      setPreviewSurface(null);
      onSetError(
        'Mini preview crashed and was disabled. Re-open View Motion to continue.',
      );
    },
    [loadedCharacterID, onSetError, selectedMotionId, setPreviewSurface],
  );

  const handleMotionStageRuntimeError = useCallback(
    (error: Error, info?: { componentStack?: string | null }) => {
      console.error('[Cartographer] Motion stage runtime error', error);
      pushCartographerDebugEvent('motionStage.runtime.error', {
        error: `${error.name}: ${error.message}`,
        errorStack: error.stack ?? null,
        componentStack: info?.componentStack ?? null,
        loadedCharacterID,
        selectedMotionId,
        previewSurface,
        canRenderMotionStage,
        motionPreviewOpen,
        cartographerAssetLibraryMode,
      });
      setPreviewSurface(null);
      onSetError(
        'In-scene motion preview crashed and was disabled. Choose Stage again to retry.',
      );
    },
    [
      canRenderMotionStage,
      cartographerAssetLibraryMode,
      loadedCharacterID,
      motionPreviewOpen,
      onSetError,
      previewSurface,
      selectedMotionId,
      setPreviewSurface,
    ],
  );

  const handleSaveAndContinueToAssetLibrary = useCallback(async () => {
    setViewMotionSaveInProgress(true);
    try {
      const didSave = await handleSaveRef.current();
      if (!didSave) return;
      setViewMotionUnsavedModalOpen(false);
      openAssetLibrary();
    } finally {
      setViewMotionSaveInProgress(false);
    }
  }, [handleSaveRef, openAssetLibrary, setViewMotionUnsavedModalOpen]);

  const handleContinueToMotionWithoutSaving = useCallback(() => {
    setViewMotionUnsavedModalOpen(false);
    openAssetLibrary();
  }, [openAssetLibrary, setViewMotionUnsavedModalOpen]);

  const assetLibraryEmbedConfig = useMemo(
    () => createMogenMotionPickerEmbedConfig(handleAssetLibraryPick),
    [handleAssetLibraryPick],
  );

  const assetLibraryPanel = (
    <AssetLibraryModal
      maxWidth={Math.max(frontDoorWidth - 16, 280)}
      embedConfig={assetLibraryEmbedConfig}
      panelStyle={{
        height: `${Math.max(cartographerAssetLibraryMode === 'motions' ? 640 : 0, frontDoorHeight - 16)}px`,
        width: `${Math.max(cartographerAssetLibraryMode === 'motions' ? 280 : 0, frontDoorWidth - 16)}px`,
      }}
      onClose={closeAssetLibrary}
    />
  );

  const motionPreviewModal = (
    <CartographerMotionPreviewModal
      key={motionPreviewSession}
      open={motionPreviewOpen}
      onOpenChange={(next: boolean) => {
        if (!next) closeMotionPreview();
      }}
      apiKey={apiKey ?? ''}
      characterID={loadedCharacterID ?? ''}
      meshPath={motionPreviewMeshUrl ?? ''}
      meshFile={motionPreviewMeshFile}
      meshFormat={motionPreviewMeshFormat ?? undefined}
      externalMotionId={motionPreviewExternalJobId}
      externalMotionLabel={motionPreviewExternalLabel}
      bodyIndex={selectedBodyIndex}
      userCharacterDisplayName={userCharacterDisplayName}
      characterMappingState={motionPreviewCharacterMappingState}
      mappingRevisionKey={motionPreviewMappingRevisionKey}
      configSource={motionPreviewConfigSource}
      prepareForUserMotionRequest={prepareForMotionRetargetRequest}
      motionExportSettings={motionExportSettings}
      onMotionExportSettingsChange={setMotionExportSettings}
      onRetargetStatusChange={handleMotionRetargetStatusChange}
      onSwitchToUnsavedRetarget={handleSwitchToUnsavedRetarget}
      onBackToMotionSearch={backFromMotionPreviewToAssetLibrary}
    />
  );

  const motionActorPickerModal = (
    <CartographerMotionActorPickerModal
      open={Boolean(actorPickerSelection)}
      motionID={actorPickerSelection?.motionID ?? null}
      bodyCount={actorPickerSelection?.bodyCount ?? 0}
      bvhURLs={actorPickerSelection?.bvhURLs}
      mhrBvhURLs={actorPickerSelection?.mhrBvhURLs}
      mhrIdentityJsonURLs={actorPickerSelection?.mhrIdentityJsonURLs}
      onOpenChange={handleActorPickerOpenChange}
      onSelectBodyIndex={handleActorPickerSelect}
    />
  );

  const motionUnsavedChangesModal = (
    <Modal
      open={viewMotionUnsavedModalOpen}
      onOpenChange={(open) => {
        if (!viewMotionSaveInProgress) {
          setViewMotionUnsavedModalOpen(open);
        }
      }}
      title="Unsaved character changes"
      description={
        <Text
          variant="studio/body/medium/sm"
          className="text-gray-60 text-[16px]/[20px]"
        >
          View Motion will preview against your current in-editor mapping without
          replacing the saved config.
          <br />
          Save now to make these changes the active saved version.
        </Text>
      }
      maxWidth={520}
    >
      <div className="flex flex-none justify-end gap-2 pt-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setViewMotionUnsavedModalOpen(false)}
          disabled={viewMotionSaveInProgress}
        >
          Cancel
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          onClick={handleContinueToMotionWithoutSaving}
          disabled={viewMotionSaveInProgress}
        >
          Continue without saving
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            void handleSaveAndContinueToAssetLibrary();
          }}
          disabled={viewMotionSaveInProgress || saveInProgress}
        >
          {viewMotionSaveInProgress ? 'Saving…' : 'Save and continue'}
        </Button>
      </div>
    </Modal>
  );

  const handleCloseMotionMini = useCallback(() => {
    rememberAndClearPreviewSurface();
  }, [rememberAndClearPreviewSurface]);

  const handleCloseMotionStage = useCallback(() => {
    rememberAndClearPreviewSurface();
  }, [rememberAndClearPreviewSurface]);

  useEffect(() => {
    if (showStageMotionPreview) return;
    setMotionStagePlaybackActive(false);
  }, [showStageMotionPreview]);

  const motionDockExtension = shouldMountDockedMotionMini ? (
    <ErrorBoundary
      fallback={null}
      onError={handleMotionMiniRuntimeError}
      resetKeys={[selectedMotionId, selectedBodyIndex, loadedCharacterID, fileVersion]}
    >
      <CartographerMotionMini
        active={showDockedMotionMini}
        apiKey={apiKey ?? ''}
        characterID={loadedCharacterID ?? ''}
        meshFile={activeCharacterFile}
        motionID={selectedMotionId}
        motionLabel={selectedMotionLabel}
        bodyIndex={selectedBodyIndex}
        fileVersion={fileVersion}
        mappingVersion={mappingVersion}
        bonePropertiesVersion={bonePropertiesVersion}
        configSource={motionPreviewConfigSource}
        prepareForUserMotionRequest={prepareForMotionRetargetRequest}
        onSwitchToUnsavedRetarget={handleSwitchToUnsavedRetarget}
        onRetargetStatusChange={handleMotionRetargetStatusChange}
        onOpenFullPreview={handleOpenFullMotionPreviewFromMini}
        onClose={handleCloseMotionMini}
      />
    </ErrorBoundary>
  ) : null;

  const motionStagePreview = showStageMotionPreview ? (
    <ErrorBoundary
      fallback={null}
      onError={handleMotionStageRuntimeError}
      resetKeys={[selectedMotionId, selectedBodyIndex, loadedCharacterID, fileVersion]}
    >
      <CartographerSceneMotionPreview
        apiKey={apiKey ?? ''}
        characterID={loadedCharacterID ?? ''}
        motionID={selectedMotionId}
        motionLabel={selectedMotionLabel}
        bodyIndex={selectedBodyIndex}
        fileVersion={fileVersion}
        mappingRevisionKey={motionPreviewMappingRevisionKey}
        assetKind={motionPreviewAssetKind}
        configSource={motionPreviewConfigSource}
        prepareForUserMotionRequest={prepareForMotionRetargetRequest}
        onSwitchToUnsavedRetarget={handleSwitchToUnsavedRetarget}
        onRetargetStatusChange={handleMotionRetargetStatusChange}
        onPlaybackActiveChange={setMotionStagePlaybackActive}
        onOpenFullPreview={handleOpenFullMotionPreviewFromMini}
        onClose={handleCloseMotionStage}
      />
    </ErrorBoundary>
  ) : null;

  const previewOptions = [
    ...(canShowMotionMini
      ? [
          {
            id: 'motion-preview-mini',
            label: 'Mini',
            compactLabel: 'Mini',
            active: previewSurface === 'mini',
            tooltipText: 'Preview the retargeted motion in the docked mini preview',
            ariaLabel: 'Preview motion in Mini mode',
            onClick: () => setPreviewSurface('mini'),
          },
        ]
      : []),
    ...(ENABLE_STAGE_MOTION_PREVIEW
      ? [
          {
            id: 'motion-preview-stage',
            label: 'Stage',
            compactLabel: 'Stage',
            active: previewSurface === 'stage',
            tooltipText:
              'Preview the retargeted motion on the stage character',
            ariaLabel: 'Preview motion in Stage mode',
            onClick: () => setPreviewSurface('stage'),
          },
        ]
      : []),
  ];
  const previewVisible = Boolean(selectedMotionId) && previewOptions.length > 0;
  const exportVisible = Boolean(currentReadyRetargetStatus);
  const isMotionRetargeting = Boolean(currentRetargetStatus?.loading);
  const exportMotionName =
    selectedMotionLabel?.trim() || selectedMotionId || 'selected motion';
  const exportDisabled =
    !activeMeshDownloadProfile ||
    motionExportBusy ||
    isMotionRetargeting ||
    Boolean(currentRetargetStatus?.mainError) ||
    Boolean(currentRetargetStatus?.previewOutOfDate);
  const exportTitle = !activeMeshDownloadProfile
    ? 'Export is available for FBX, GLB, or GLTF characters'
    : motionExportBusy
      ? `Exporting ${exportMotionName}`
      : isMotionRetargeting
        ? `Retargeting ${exportMotionName} is in progress`
        : currentRetargetStatus?.mainError
          ? `Retargeting failed for ${exportMotionName}. Refresh the preview before exporting.`
          : currentRetargetStatus?.previewOutOfDate
            ? `Retarget ${exportMotionName} with the current mapping before exporting`
            : `Export Retargeted Motion "${exportMotionName}" as ${activeMeshDownloadProfile.label}`;

  const motionSection: ViewerToolDockMotionSectionProps = {
    onChooseMotionClick: requestOpenAssetLibrary,
    chooseMotionDisabled: !apiKey || !loadedCharacterID || !hasGltfScene,
    previewVisible,
    onPreviewClick: toggleToolDockMotionPreview,
    previewDisabled: !previewVisible,
    previewTitle: 'Choose where to preview the selected motion',
    previewActive: previewVisible && Boolean(previewSurface),
    previewOptions,
    previewOptionsOpen: previewOptionsOpen && previewVisible,
    previewExtension: motionDockExtension,
    previewExtensionOpen: showDockedMotionMini,
    exportVisible,
    exportDisabled,
    exportBusy: motionExportBusy,
    exportTitle,
    onExportClick: handleDockMotionExport,
  };

  return {
    canRenderMotionMini,
    cartographerAssetLibraryMode,
    assetLibraryPanel,
    motionActorPickerModal,
    motionMiniToggleDisabled,
    motionMiniToggleLabel,
    motionPreviewModal,
    motionPreviewSurface: previewSurface,
    motionStagePlaybackActive,
    selectedMotionId,
    selectedMotionLabel,
    selectedBodyIndex,
    isMotionRetargeting,
    motionStagePreview,
    motionSection,
    motionUnsavedChangesModal,
    resetMotionMiniState,
    toggleMotionMini,
  };
}
